import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@renderer/chat/providers'
import { useUser } from '@renderer/chat/store'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import { useIsGatewayConnected } from '@renderer/overlay/atoms'

export interface StagedActionJob {
  id: string
  sourceId: string | null
  status: 'queued' | 'generating' | 'completed' | 'idle' | 'error' | (string & {})
  details?: string | null
  toolsets?: Array<{
    name: string
    count: number
  }> | null
}

interface StagedActionsJobsResponse {
  success: true
  data: Array<StagedActionJob>
}

export const STAGED_ACTIONS_JOBS_QUERY_KEY = 'staged-actions-jobs'

/** A run that dies never pushes a status, so poll while a job is active and let the server expire it. */
function resolveJobsRefetchInterval(
  isGatewayConnected: boolean,
  jobs: Array<StagedActionJob> | undefined,
): number | false {
  if (!isGatewayConnected) {
    return 10 * 1000
  }
  const hasActiveJob = jobs?.some((job) => job.status === 'queued' || job.status === 'generating') ?? false
  return hasActiveJob ? 60 * 1000 : false
}

export function useStagedActionJobs() {
  const { getAuthToken } = useAuth()
  const user = useUser()
  const isGatewayConnected = useIsGatewayConnected()

  const isSignedIn = !!user && !user.isAnonymous

  const query = useQuery({
    queryKey: [STAGED_ACTIONS_JOBS_QUERY_KEY],
    queryFn: async () => {
      try {
        const token = await getAuthToken()
        const response = await fetch(getChatApiRoute('api/v1/staged-actions/jobs'), {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (!response.ok) {
          throw new Error('Failed to fetch staged action jobs')
        }

        const json = (await response.json()) as StagedActionsJobsResponse
        return json.data
      } catch (error) {
        console.error('Failed to fetch staged action jobs:', error)
        throw error
      }
    },
    enabled: isSignedIn,
    staleTime: 30 * 1000,
    refetchInterval: (query) => resolveJobsRefetchInterval(isGatewayConnected, query.state.data),
    refetchIntervalInBackground: !isGatewayConnected,
    refetchOnWindowFocus: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })

  return {
    ...query,
    jobs: query.data ?? [],
  }
}

export function useSetStagedActionJobs() {
  const queryClient = useQueryClient()

  return (jobs: Array<StagedActionJob>) => {
    queryClient.setQueryData([STAGED_ACTIONS_JOBS_QUERY_KEY], jobs)
  }
}
